// Alert Config API Functions
// Feature: 015-we-want-to
// T059-T063: API client for alert configurations

import { apiClient } from './client';

export type NotificationType =
  | 'COLLECTION_NEW_LISTING'
  | 'COLLECTION_NEW_FLOOR'
  | 'COLLECTION_SALE'
  | 'COLLECTION_MINT';

export type AlertDeliveryChannel = 'inbox' | 'discord';

export interface TraitFilters {
  [traitType: string]: string[];
}

export interface AlertConfig {
  id: string;
  user_id: string;
  collection_id: string;
  notification_type: NotificationType;
  min_amount_in_xrp: number | null;
  max_amount_in_xrp: number | null;
  trait_filters: TraitFilters | null;
  delivery_channel: AlertDeliveryChannel;
  discord_webhook_url: string | null;
  discord_enabled: boolean;
  created_at: string;
}

export interface CreateAlertConfigInput {
  collection_id: string;
  notification_type: NotificationType;
  min_amount_in_xrp?: number | null;
  max_amount_in_xrp?: number | null;
  trait_filters?: TraitFilters | null;
  delivery_channel?: AlertDeliveryChannel;
  discord_webhook_url?: string | null;
  discord_enabled?: boolean;
}

export interface UpdateAlertConfigInput {
  min_amount_in_xrp?: number | null;
  max_amount_in_xrp?: number | null;
  trait_filters?: TraitFilters | null;
  delivery_channel?: AlertDeliveryChannel;
  discord_webhook_url?: string | null;
  discord_enabled?: boolean;
}

export interface AlertConfigsResponse {
  alert_configs: AlertConfig[];
  count: number;
}

export interface AlertConfigResponse {
  alert_config: AlertConfig;
}

export interface AlertConfigsByCollectionResponse {
  alert_configs: AlertConfig[];
  collection_id: string;
  count: number;
}

export interface DeleteAlertConfigResponse {
  message: string;
  alert_config_id: string;
}

/**
 * Fetch all alert configurations for the authenticated user
 */
export async function fetchAlertConfigs(): Promise<AlertConfigsResponse> {
  return apiClient.get<AlertConfigsResponse>('/alert-configs');
}

/**
 * Fetch alert configurations for a specific collection
 */
export async function fetchAlertConfigsByCollection(
  collectionId: string
): Promise<AlertConfigsByCollectionResponse> {
  return apiClient.get<AlertConfigsByCollectionResponse>(
    `/alert-configs/by-collection/${collectionId}`
  );
}

/**
 * Create a new alert configuration
 */
export async function createAlertConfig(
  input: CreateAlertConfigInput
): Promise<AlertConfigResponse> {
  return apiClient.post<AlertConfigResponse>('/alert-configs', input);
}

/**
 * Update an alert configuration
 */
export async function updateAlertConfig(
  alertConfigId: string,
  input: UpdateAlertConfigInput
): Promise<AlertConfigResponse> {
  return apiClient.patch<AlertConfigResponse>(
    `/alert-configs/${alertConfigId}`,
    input
  );
}

/**
 * Delete an alert configuration
 */
export async function deleteAlertConfig(
  alertConfigId: string
): Promise<DeleteAlertConfigResponse> {
  return apiClient.delete<DeleteAlertConfigResponse>(
    `/alert-configs/${alertConfigId}`
  );
}

export interface TestWebhookResponse {
  success: boolean;
  message: string;
  error?: string;
}

/**
 * Test a Discord webhook URL by sending a test message
 */
export async function testDiscordWebhook(
  webhookUrl: string
): Promise<TestWebhookResponse> {
  return apiClient.post<TestWebhookResponse>('/alert-configs/test-webhook', {
    webhook_url: webhookUrl,
  });
}

/**
 * Get human-readable label for notification type
 */
export function getNotificationTypeLabel(type: NotificationType): string {
  switch (type) {
    case 'COLLECTION_NEW_LISTING':
      return 'New Listing';
    case 'COLLECTION_NEW_FLOOR':
      return 'Floor Price Change';
    case 'COLLECTION_SALE':
      return 'Sale';
    case 'COLLECTION_MINT':
      return 'Mint';
    default:
      return type;
  }
}

/**
 * Get description for notification type
 */
export function getNotificationTypeDescription(type: NotificationType): string {
  switch (type) {
    case 'COLLECTION_NEW_LISTING':
      return 'Get notified when new NFTs are listed in this collection';
    case 'COLLECTION_NEW_FLOOR':
      return 'Get notified when the floor price changes';
    case 'COLLECTION_SALE':
      return 'Get notified when NFTs are sold in this collection';
    case 'COLLECTION_MINT':
      return 'Get notified when new NFTs are minted in this collection';
    default:
      return '';
  }
}
